import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { repositoryAPI } from '../../services/api';
import { repoKeys } from '../../query/queryKeys';
import {
  mergeRepoCycleConfigForForm,
  buildCycleTimeConfigPayload,
  parseBotLoginsText,
} from '../../constants/cycleTimeConfig';
import toast from '../../services/toast';
import { getRepoDeploymentBranches } from '../../utils/deploymentBranches';

/**
 * Settings modal branch state and save handler for repository detail.
 */
export function useRepositoryBranchSettings({ id, repository, onSync }: any) {
  const queryClient = useQueryClient();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [selectedDeploymentBranches, setSelectedDeploymentBranches] = useState<string[]>([]);
  const [customDeploymentBranchesText, setCustomDeploymentBranchesText] = useState('');
  const [savingBranch, setSavingBranch] = useState(false);
  const [settingsQaBranch, setSettingsQaBranch] = useState('');
  const [settingsUatBranch, setSettingsUatBranch] = useState('');
  const [cycleBotLoginsText, setCycleBotLoginsText] = useState('');
  const [cycleSendNullOnSave, setCycleSendNullOnSave] = useState(false);

  const openSettings = async () => {
    setSettingsOpen(true);
    const deployBranches = getRepoDeploymentBranches(repository);
    setSelectedDeploymentBranches(deployBranches);
    setCustomDeploymentBranchesText(deployBranches.join(', '));
    setSettingsQaBranch(repository?.qaBranch || '');
    setSettingsUatBranch(repository?.uatBranch || '');
    const cycleMerged = mergeRepoCycleConfigForForm(repository?.cycleTimeConfig);
    setCycleBotLoginsText(cycleMerged.botLogins.join('\n'));
    setCycleSendNullOnSave(false);
    setBranchesLoading(true);
    setBranches([]);
    try {
      const res = await repositoryAPI.getBranches(id, { limit: 500 });
      const list = res.branches || [];
      setBranches(list);
    } catch {
      setBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  };

  const handleSaveBranches = async () => {
    let deploymentBranches: string[] = [];
    if (branches.length > 0) {
      deploymentBranches = selectedDeploymentBranches.map((b) => b.trim()).filter(Boolean);
    } else {
      deploymentBranches = customDeploymentBranchesText
        .split(',')
        .map((b) => b.trim())
        .filter(Boolean);
    }
    if (deploymentBranches.length === 0) {
      toast.error('Select at least one deployment branch');
      return;
    }
    setSavingBranch(true);
    try {
      const cycleTimeConfig = cycleSendNullOnSave
        ? null
        : buildCycleTimeConfigPayload({
            botLogins: parseBotLoginsText(cycleBotLoginsText),
          });
      const res = await repositoryAPI.update(id, {
        deploymentBranches,
        qaBranch: settingsQaBranch.trim() || null,
        uatBranch: settingsUatBranch.trim() || null,
        cycleTimeConfig,
      });
      if (res.repository) queryClient.setQueryData(repoKeys.byId(id), res.repository);
      toast.success('Branch settings saved');
      setSettingsOpen(false);
      await onSync();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update branch settings');
    } finally {
      setSavingBranch(false);
    }
  };

  const handleResetCycleBotDefaults = () => {
    const d = mergeRepoCycleConfigForForm(null);
    setCycleBotLoginsText(d.botLogins.join('\n'));
    setCycleSendNullOnSave(true);
  };

  const handleCycleBotLoginsInteraction = () => setCycleSendNullOnSave(false);

  return {
    settingsOpen,
    setSettingsOpen,
    branches,
    branchesLoading,
    selectedDeploymentBranches,
    setSelectedDeploymentBranches,
    customDeploymentBranchesText,
    setCustomDeploymentBranchesText,
    savingBranch,
    settingsQaBranch,
    setSettingsQaBranch,
    settingsUatBranch,
    setSettingsUatBranch,
    cycleBotLoginsText,
    setCycleBotLoginsText,
    openSettings,
    handleSaveBranches,
    handleResetCycleBotDefaults,
    handleCycleBotLoginsInteraction,
  };
}
